import { Box, Typography, useTheme } from '@mui/material'
import React, { memo, useCallback, useContext, useMemo } from 'react'
import { ExtraSmallButton, NonInteractiveTooltip } from '../../../custom'
import { LineData, Time } from 'lightweight-charts'
import './seriesMetadataDisplay.css'
import { useDispatch, useSelector } from 'react-redux'
import {
    selectMultiStrikeChartCallPut,
    selectMultiStrikeChartSecurities,
    selectMultiStrikeChartType
} from '../../../../selectors/multiStrikeChart'
import { formatLargeNumber } from '../../../../util/screener'
import { AppContext } from '../../../../App'
import {
    MultiStrikeSecuritiesClassType,
    setMultiStrikeNearAtm,
    setMultiStrikeSecurities,
    setMultiStrikeShowCallPut
} from '../../../../reducer/multiStrikeChart.reducer'
import { updateMultiStrikeChartConfig } from '../../../../reducer/chart.reducer'
import { ChartViewKey } from '../../../../assets/constants'
import {
    selectMultiStrikeChartConfigCallPut,
    selectMultiStrikeChartConfigChartType,
    selectMultiStrikeChartConfigSecurities
} from '../../../../selectors/chart'
import BinIcon from '../../../../assets/icons/BinIcon'

export type SeriesMetadataProps = {
    strike: string
    callSeriesData: LineData<Time> | undefined
    putSeriesData: LineData<Time> | undefined
    callSeriesColor: string | undefined
    putSeriesColor: string | undefined
    callKey: string | undefined
    putKey: string | undefined
    hoverdTime: number
    viewKey: string
    multiStrikeClassType: MultiStrikeSecuritiesClassType
}

export function findNearestIndex(arr: any, target: number) {
    let low = 0
    let high = arr.length - 1
    let result = -1
    while (low <= high) {
        const mid = Math.floor((low + high) / 2)

        if (arr[mid].time <= target) {
            result = mid
            low = mid + 1
        } else {
            high = mid - 1
        }
    }
    return result
}

const SeriesOptionMetadataDisplay: React.FC<SeriesMetadataProps> = ({
    strike,
    callSeriesData,
    putSeriesData,
    putSeriesColor,
    callSeriesColor,
    callKey,
    putKey,
    hoverdTime,
    viewKey,
    multiStrikeClassType
}) => {
    const theme = useTheme()
    const dispatch = useDispatch()
    const isWidget = useMemo(() => viewKey !== ChartViewKey.MULTI_STRIKES_CHART, [viewKey])
    const chartType = useSelector(isWidget ? selectMultiStrikeChartConfigChartType(viewKey) : selectMultiStrikeChartType)
    const appContext = useContext(AppContext)
    const multiStrikeChartSecurities = useSelector(isWidget ? selectMultiStrikeChartConfigSecurities(viewKey) : selectMultiStrikeChartSecurities)
    const chart = appContext.getMultiStrikeChart(viewKey)
    const cumOiCallPut = useSelector(isWidget ? selectMultiStrikeChartConfigCallPut(viewKey) : selectMultiStrikeChartCallPut)
    const callPut = chartType === 'cumOi' ? cumOiCallPut : multiStrikeChartSecurities?.[strike]

    const removeStrike = useCallback(() => {
        const chart = appContext.getMultiStrikeChart(viewKey)
        if (chart) {
            const newMultiStrikeChartSecurities = { ...multiStrikeChartSecurities }
            chart.dataManager.removeStrike(strike)
            delete newMultiStrikeChartSecurities[strike]
            if (isWidget) {
                dispatch(updateMultiStrikeChartConfig({
                    viewKey, config: {
                        securities: newMultiStrikeChartSecurities,
                        nearAtm: undefined,
                        atm: undefined
                    }
                }))
            } else {
                dispatch(setMultiStrikeSecurities(newMultiStrikeChartSecurities))
                dispatch(setMultiStrikeNearAtm({
                    nearAtm: undefined,
                    atm: undefined
                }))
            }
        }
    }, [appContext, dispatch, isWidget, multiStrikeChartSecurities, strike, viewKey])

    const callValue: number | undefined = useMemo(() => {
        if (callSeriesData) return callSeriesData.value
        if (chart && callKey) {
            const data: any = chart.dataManager.seriesApis?.[callKey]?.data
            if (data) {
                let index = findNearestIndex(data, hoverdTime)
                let value = undefined
                while (value === undefined && index >= 0) {
                    value = data[index]?.value
                    index = index - 1
                }
                return value
            }
        }
        return undefined
    }, [callKey, callSeriesData, chart, hoverdTime])

    const putValue: number | undefined = useMemo(() => {
        if (putSeriesData) return putSeriesData.value
        if (chart && putKey) {
            const data: any = chart.dataManager.seriesApis?.[putKey]?.data
            if (data) {
                let index = findNearestIndex(data, hoverdTime)
                let value = undefined
                while (value === undefined && index >= 0) {
                    value = data[index]?.value
                    index = index - 1
                }
                return value
            }
        }
        return undefined
    }, [chart, hoverdTime, putKey, putSeriesData])

    const handleCumOiCallPutChange = useCallback(
        (isCall: boolean) => {
            let updatedCallPut = [...(callPut ?? ['CE', 'PE'])]
            const valueToToggle = isCall ? 'CE' : 'PE'
            if (updatedCallPut.includes(valueToToggle)) {
                updatedCallPut = updatedCallPut.filter((item) => item !== valueToToggle)
            } else {
                updatedCallPut.push(valueToToggle)
            }
            if (isWidget) {
                dispatch(updateMultiStrikeChartConfig({ viewKey, config: { callPut: updatedCallPut } }))
            } else {
                dispatch(setMultiStrikeShowCallPut(updatedCallPut))
            }
        },
        [dispatch, isWidget, viewKey, callPut]
    )

    const handleCallPutChange = useCallback(
        (isCall: boolean) => {
            const updatedSecurities = { ...multiStrikeChartSecurities }
            if (updatedSecurities[strike]) {
                let security = updatedSecurities[strike]
                const toggleType = isCall ? 'CE' : 'PE'
                if (security.includes(toggleType)) {
                    security = security.filter((item) => item !== toggleType) // Remove if it exists
                } else {
                    security = [...security, toggleType] // Add if it doesn't exist
                }
                updatedSecurities[strike] = security

                const callPutSecurities: MultiStrikeSecuritiesClassType = {}
                Object.keys(updatedSecurities).forEach(strike => {
                    callPutSecurities[strike] = {
                        callPut: updatedSecurities[strike],
                        callSecKey: multiStrikeClassType[strike]?.callSecKey,
                        putSecKey: multiStrikeClassType[strike]?.putSecKey
                    }
                })

                if (isWidget) {
                    dispatch(updateMultiStrikeChartConfig({ viewKey, config: { securities: updatedSecurities } }))
                } else {
                    dispatch(setMultiStrikeSecurities(updatedSecurities))
                }
                chart.updateStrikeCallPutVisible(callPutSecurities)
            }
        },
        [multiStrikeChartSecurities, strike, isWidget, chart, multiStrikeClassType, dispatch, viewKey]
    )
    return <Typography
        color={'textSecondary'}
        className="series-name flex items-center"
        component={'div'}
        sx={{
            borderColor: 'transparent',
            width: 'fit-content',
            fontSize: '14px',
            height: '24px',
            pl: '4px',
            border: `1px solid ${theme.palette.background.paper}`,
            borderRadius: '4px',
            '&:hover': {
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                boxSizing: 'border-box'
            },
            '&:hover .setting-btn': {
                visibility: 'visible !important'
            }
        }}
    >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, fontWeight: 500, textWrap: 'nowrap', fontSize: '13px' }}
            className="font-feature-num">
            {strike}

            <>
                <NonInteractiveTooltip placement="top" title={callPut?.includes('CE') ? 'hide' : 'show'}
                    arrow><span
                        onClick={() =>
                            chartType === 'cumOi'
                                ? handleCumOiCallPutChange(true)
                                : handleCallPutChange(true)
                        }
                        style={{
                            cursor: 'pointer',
                            textDecoration: callPut?.includes('CE') ? 'none' : 'line-through',
                        }}
                    >
                        {' '}CE{' '}
                    </span>
                </NonInteractiveTooltip>
                <span
                    style={{
                        color: callSeriesColor ?? theme.palette.text.primary,
                        opacity: callPut?.includes('CE') ? 1 : 0.3,
                    }}
                >
                    {callPut?.includes('CE') && callValue
                        ? ['oi', 'cumOi'].includes(chartType)
                            ? formatLargeNumber(callValue, 2)
                            : callValue.toFixed(2)
                        : '-'}
                </span>
            </>
            <>
                <NonInteractiveTooltip placement="top" title={callPut?.includes('PE') ? 'hide' : 'show'} >
                    <span
                        onClick={() =>
                            chartType === 'cumOi'
                                ? handleCumOiCallPutChange(false)
                                : handleCallPutChange(false)
                        }
                        style={{
                            cursor: 'pointer',
                            textDecoration: callPut?.includes('PE') ? 'none' : 'line-through',
                        }}
                    >
                        {' '}PE{' '}
                    </span>
                </NonInteractiveTooltip>
                <span
                    style={{
                        color: putSeriesColor ?? theme.palette.text.primary,
                        opacity: callPut?.includes('PE') ? 1 : 0.3,
                    }}
                >
                    {callPut?.includes('PE') && putValue
                        ? ['oi', 'cumOi'].includes(chartType)
                            ? formatLargeNumber(putValue, 2)
                            : putValue.toFixed(2)
                        : '-'}
                </span>
            </>

        </Box>
        {
            !!strike &&
            <NonInteractiveTooltip placement={'top'} title={'Remove'} arrow>
                <ExtraSmallButton
                    color="inherit"
                    size="small"
                    className="setting-btn"
                    onClick={removeStrike}
                    data-event={'Multi strike chat remove series clicked'}
                    data-event-series-name={callKey + ' ' + putKey}
                    style={{ visibility: 'hidden', height: '18px' }}
                >
                    <BinIcon sx={{ fontSize: '16px' }} />
                </ExtraSmallButton>
            </NonInteractiveTooltip>
        }
    </Typography>
}
export default memo(SeriesOptionMetadataDisplay)
